/**
 * Announcement bar — vanilla JS (no Vue).
 * Reads window.__ANNOUNCEMENT_BAR__ from Liquid (snippets/block__general--announcement-bar.liquid).
 */
(function () {
  'use strict';
  let cachedBanners = null;
  let schedulerTimer = null;
  let lastRenderedKey = '';

  function getSettings() {
    return window.__ANNOUNCEMENT_BAR__;
  }

  function timeStringToMinutes(timeString) {
    if (!timeString) return 0;
    const parts = String(timeString).split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  }

  function convertDateFormat(ddmmyyyy) {
    if (!ddmmyyyy) return null;
    const parts = String(ddmmyyyy).split('-');
    if (parts.length < 3) return null;
    return parts[2] + '-' + parts[1] + '-' + parts[0];
  }

  function getTimeInSchedulerTimezone(timezone, nowMs) {
    const now = new Date(nowMs != null ? nowMs : Date.now());
    return new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  }

  function getCurrentTimeInMinutes(settings, nowMs) {
    const tz = settings.timeScheduler.timezone;
    const now = new Date(nowMs != null ? nowMs : Date.now());
    const schedulerTime = new Date(now.toLocaleString('en-US', { timeZone: tz }));
    return schedulerTime.getHours() * 60 + schedulerTime.getMinutes();
  }

  function isTimeInRange(startTime, endTime, currentMinutes) {
    const startMinutes = timeStringToMinutes(startTime);
    const endMinutes = timeStringToMinutes(endTime);
    if (endMinutes < startMinutes) {
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    }
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }

  function isDateInRange(settings, startDate, endDate, nowMs) {
    if (!startDate && !endDate) return true;
    const tz = settings.timeScheduler.timezone;
    const schedulerTime = getTimeInSchedulerTimezone(tz, nowMs);
    const todayInTimezone = schedulerTime.toLocaleDateString('en-CA', { timeZone: tz });
    const startFormatted = startDate ? convertDateFormat(startDate) : null;
    const endFormatted = endDate ? convertDateFormat(endDate) : null;
    if (startFormatted && todayInTimezone < startFormatted) return false;
    if (endFormatted && todayInTimezone > endFormatted) return false;
    return true;
  }

  function matchedCustomMessage(settings, pathname) {
    const origin = window.location.origin;
    const list = settings.customMessages || [];
    for (let i = 0; i < list.length; i++) {
      const customMsg = list[i];
      if (customMsg.url && String(customMsg.url).trim() !== '') {
        const cleanUrl = String(customMsg.url).trim().replace(origin, '');
        if (pathname === cleanUrl || pathname.startsWith(cleanUrl + '/')) {
          return customMsg;
        }
      }
    }
    return null;
  }

  function scheduleEnabled(s) {
    return s.enabled !== false && s.enabled !== 'false';
  }

  function getActiveSchedule(settings, nowMs) {
    const schedules = settings.timeScheduler && settings.timeScheduler.schedules;
    if (!schedules || schedules.length === 0) return null;
    const currentMinutes = getCurrentTimeInMinutes(settings, nowMs);
    for (let i = 0; i < schedules.length; i++) {
      const schedule = schedules[i];
      if (
        scheduleEnabled(schedule) &&
        isDateInRange(settings, schedule.startDate, schedule.endDate, nowMs) &&
        isTimeInRange(schedule.startTime, schedule.endTime, currentMinutes)
      ) {
        return schedule;
      }
    }
    return null;
  }

  function getDisplayContent(settings, pathname, nowMs) {
    const mm = matchedCustomMessage(settings, pathname);
    if (mm) {
      return {
        text: mm.text && String(mm.text).trim() !== '' ? mm.text : settings.defaultText,
        link: mm.link && String(mm.link).trim() !== '' ? mm.link : settings.defaultLink,
      };
    }
    const schedule = getActiveSchedule(settings, nowMs);
    if (schedule) {
      return {
        text:
          schedule.message && String(schedule.message).trim() !== ''
            ? schedule.message
            : settings.defaultText,
        link:
          schedule.link && String(schedule.link).trim() !== ''
            ? schedule.link
            : settings.defaultLink,
      };
    }
    return {
      text: settings.defaultText,
      link: settings.defaultLink,
    };
  }

  function getBanners() {
    if (cachedBanners && cachedBanners.length) return cachedBanners;
    cachedBanners = Array.prototype.slice.call(document.querySelectorAll('#announcement-bar-app .banner__link'));
    return cachedBanners;
  }

  function updateBanner(settings) {
    const content = getDisplayContent(settings, window.location.pathname, Date.now());
    const renderKey = [content.text || '', content.link || ''].join('|');
    if (renderKey === lastRenderedKey) return false;

    const banners = getBanners();
    if (!banners.length) return false;

    let updated = false;
    banners.forEach(function (banner) {
      if (
        (window.innerWidth >= 768 && banner.classList.contains('banner__link--duplicate')) ||
        banner.classList.contains('banner__link--duplicate')
      ) {
        return;
      }
      const bannerText = banner.querySelector('.banner__text');
      const currentText = bannerText ? bannerText.textContent : banner.textContent;
      const href = banner.getAttribute('href') || '';
      const nextLink = content.link || '';
      const needsUpdate = currentText !== content.text || href !== nextLink;
      if (needsUpdate) {
        if (bannerText) {
          bannerText.textContent = content.text;
        } else {
          banner.textContent = content.text;
        }
        banner.setAttribute('href', nextLink || '#');
        banner.onclick = null;
        updated = true;
      }
    });

    if (updated) {
      lastRenderedKey = renderKey;
    }

    if (updated && typeof window.enableMarqueeIfOverflow === 'function') {
      setTimeout(window.enableMarqueeIfOverflow, 100);
    }
    return updated;
  }

  function clearSchedulerTimer() {
    if (schedulerTimer) {
      clearTimeout(schedulerTimer);
      schedulerTimer = null;
    }
  }

  function scheduleNextBannerUpdate(settings) {
    clearSchedulerTimer();
    if (document.visibilityState === 'hidden') return;

    const now = Date.now();
    const delayToNextMinute = 60000 - (now % 60000);
    schedulerTimer = setTimeout(function () {
      updateBanner(settings);
      scheduleNextBannerUpdate(settings);
    }, Math.max(1000, delayToNextMinute));
  }

  function init() {
    const settings = getSettings();
    if (!settings) return;

    var root = document.getElementById('announcement-bar-app');
    if (root) {
      root.classList.remove('banner--loading');
    }

    updateBanner(settings);

    if (settings.timeScheduler && settings.timeScheduler.schedules && settings.timeScheduler.schedules.length > 0) {
      scheduleNextBannerUpdate(settings);
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
          updateBanner(settings);
          scheduleNextBannerUpdate(settings);
        } else {
          clearSchedulerTimer();
        }
      });
    }

    if (typeof window.enableMarqueeIfOverflow === 'function') {
      requestAnimationFrame(window.enableMarqueeIfOverflow);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
